import { useEffect, useState } from "react";

import {
  deleteAdminGuestProfile,
  getAdminGuestBonusHistory,
  patchAdminGuestProfile,
  postAdminSetGuestHostess,
  postAdminRestoreChannelBalance,
} from "../../api/api";
import { Button } from "../../components/Button";
import { formatIrkutskDateTime } from "../../lib/time";

function getBonusKindLabel(kind) {
  if (kind === "promo_profile") return "акция ФИО";
  if (kind === "promo_birthday") return "акция дата рождения";
  if (kind === "promo_phone") return "акция телефон";
  if (kind === "promo_triple") return "бонус за 4 шага";
  if (kind === "promo_yandex_review") return "акция отзыв Яндекс";
  if (kind === "promo_2gis_review") return "акция отзыв 2ГИС";
  if (kind === "referral_inviter") return "реферальный бонус";
  if (kind === "birthday_loyalty") return "подарок на день рождения";
  if (kind === "ch_forfeit") return "удержание (канал)";
  if (kind === "ch_restore") return "восстановление (канал)";
  if (kind === "raffle_win") return "выигрыш в розыгрыше";
  if (kind === "redeem") return "QR начисление";
  if (kind === "debit_redeem") return "QR списание";
  if (kind === "admin_adj") return "корректировка админа";
  return kind || "операция";
}

export function AdminGuestBaseSection({
  loading,
  rows,
  search,
  setSearch,
  onRefresh,
  mode = "guestBase",
  canManageHostess = true,
}) {
  const hostessMode = mode === "hostess";
  const [localQ, setLocalQ] = useState(search);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    profile_last_name: "",
    profile_first_name: "",
    profile_patronymic: "",
    profile_nickname: "",
    profile_phone: "",
    profile_birth_date: "",
    bonus_balance: "",
  });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [togglingHostessId, setTogglingHostessId] = useState(null);
  const [hostessError, setHostessError] = useState("");
  const [restoringChannelId, setRestoringChannelId] = useState(null);
  const [restoreChannelError, setRestoreChannelError] = useState("");
  const [openedHistoryUserId, setOpenedHistoryUserId] = useState(null);
  const [historyByUserId, setHistoryByUserId] = useState({});
  const [loadingHistoryUserId, setLoadingHistoryUserId] = useState(null);
  const [historyError, setHistoryError] = useState("");

  useEffect(() => {
    setLocalQ(search);
  }, [search]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (localQ !== search) setSearch(localQ);
    }, 320);
    return () => window.clearTimeout(t);
  }, [localQ, search, setSearch]);

  const startEdit = (row) => {
    setEditError("");
    setEditingId(row.id);
    setEditDraft({
      profile_last_name: row.profile_last_name || "",
      profile_first_name: row.profile_first_name || "",
      profile_patronymic: row.profile_patronymic || "",
      profile_nickname: row.profile_nickname || "",
      profile_phone: row.profile_phone || "",
      profile_birth_date: row.profile_birth_date
        ? String(row.profile_birth_date).slice(0, 10)
        : "",
      bonus_balance: String(row.bonus_balance ?? 0),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError("");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setEditError("");
    try {
      const rawBonus = String(editDraft.bonus_balance).trim();
      const bonusParsed = rawBonus === "" ? NaN : parseInt(rawBonus, 10);
      const bonus_balance =
        Number.isFinite(bonusParsed) && bonusParsed >= 0 ? bonusParsed : 0;

      await patchAdminGuestProfile(editingId, {
        profile_last_name: editDraft.profile_last_name.trim(),
        profile_first_name: editDraft.profile_first_name.trim(),
        profile_patronymic: editDraft.profile_patronymic.trim(),
        profile_nickname: editDraft.profile_nickname.trim() || null,
        profile_phone: editDraft.profile_phone.trim(),
        profile_birth_date: editDraft.profile_birth_date.trim()
          ? editDraft.profile_birth_date.trim()
          : null,
        bonus_balance,
      });
      setEditingId(null);
      onRefresh?.();
    } catch (err) {
      setEditError(err?.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const onRestoreChannelBalance = async (row) => {
    setRestoreChannelError("");
    setRestoringChannelId(row.id);
    try {
      await postAdminRestoreChannelBalance(row.id);
      onRefresh?.();
    } catch (err) {
      setRestoreChannelError(err?.message || "Не удалось восстановить баланс");
    } finally {
      setRestoringChannelId(null);
    }
  };

  const onDeleteGuest = async (row) => {
    const label =
      [row.profile_last_name, row.profile_first_name, row.profile_patronymic]
        .filter(Boolean)
        .join(" ")
        .trim() || `Telegram id ${row.telegram_id}`;
    const ok = window.confirm(
      `Удалить гостя «${label}» из базы безвозвратно?\n\n` +
        "Будут стёрты: все его операции по бонусам, погашенные им QR-коды, брони маркета. " +
        "У других пользователей записи не трогаем (в т.ч. реферальный бонус пригласившему). " +
        "Если этот человек снова зайдёт по реферальной ссылке, он зарегистрируется заново и реферал сработает снова.",
    );
    if (!ok) return;
    setDeletingId(row.id);
    setDeleteError("");
    try {
      await deleteAdminGuestProfile(row.id);
      if (editingId === row.id) setEditingId(null);
      onRefresh?.();
    } catch (err) {
      setDeleteError(err?.message || "Не удалось удалить");
    } finally {
      setDeletingId(null);
    }
  };

  const onToggleHostess = async (row) => {
    if (!canManageHostess) return;
    const actionLabel = row.is_hostess ? "снять роль хостес" : "назначить хостес";
    const personLabel =
      [row.profile_last_name, row.profile_first_name, row.profile_patronymic]
        .filter(Boolean)
        .join(" ")
        .trim() || `Telegram id ${row.telegram_id}`;
    const ok = window.confirm(`Подтвердите: ${actionLabel} для «${personLabel}»?`);
    if (!ok) return;
    setHostessError("");
    setTogglingHostessId(row.id);
    try {
      await postAdminSetGuestHostess(row.id, !row.is_hostess);
      onRefresh?.();
    } catch (err) {
      setHostessError(err?.message || "Не удалось обновить роль хостес");
    } finally {
      setTogglingHostessId(null);
    }
  };

  const onToggleBonusHistory = async (row) => {
    setHistoryError("");
    if (openedHistoryUserId === row.id) {
      setOpenedHistoryUserId(null);
      return;
    }
    setOpenedHistoryUserId(row.id);
    if (historyByUserId[row.id]) return;
    setLoadingHistoryUserId(row.id);
    try {
      const items = await getAdminGuestBonusHistory(row.id, 50);
      setHistoryByUserId((prev) => ({
        ...prev,
        [row.id]: Array.isArray(items) ? items : [],
      }));
    } catch (err) {
      setHistoryError(err?.message || "Не удалось загрузить историю бонусов");
    } finally {
      setLoadingHistoryUserId(null);
    }
  };

  return (
    <>
      <form className="adminForm" onSubmit={(e) => e.preventDefault()}>
        <h3>{hostessMode ? "Управление хостес" : "База гостей"}</h3>
        <label className="field">
          Поиск
          <input
            type="search"
            placeholder={
              hostessMode
                ? "Поиск по ФИО, username, Telegram id (без поиска — текущие хостес)"
                : "Фамилия, имя, ник, username, имя в Telegram или Telegram id"
            }
            value={localQ}
            onChange={(e) => setLocalQ(e.target.value)}
          />
        </label>
        {onRefresh ? (
          <Button type="button" onClick={onRefresh}>
            Обновить список
          </Button>
        ) : null}
        {deleteError ? <p className="errorMessage">{deleteError}</p> : null}
        {hostessError ? <p className="errorMessage">{hostessError}</p> : null}
        {restoreChannelError ? <p className="errorMessage">{restoreChannelError}</p> : null}
        {historyError ? <p className="errorMessage">{historyError}</p> : null}
      </form>

      <section className="adminGroup">
        <h3>Записей: {rows.length}</h3>
        {loading ? <p className="muted">Загрузка…</p> : null}
        {!loading && rows.length === 0 ? (
          <p className="muted">Никого не найдено.</p>
        ) : null}
        {!loading
          ? rows.map((row) => (
              <article
                key={row.id}
                className={[
                  "adminItem",
                  editingId === row.id ? "adminItem--stack" : "",
                  row.is_hostess ? "adminItem--hostess" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {editingId === row.id ? (
                  <div className="adminGuestBase__edit">
                    <h4>Редактирование (Telegram id {row.telegram_id})</h4>
                    <label className="field">
                      Фамилия
                      <input
                        type="text"
                        value={editDraft.profile_last_name}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, profile_last_name: e.target.value }))
                        }
                      />
                    </label>
                    <label className="field">
                      Имя
                      <input
                        type="text"
                        value={editDraft.profile_first_name}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, profile_first_name: e.target.value }))
                        }
                      />
                    </label>
                    <label className="field">
                      Отчество
                      <input
                        type="text"
                        value={editDraft.profile_patronymic}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, profile_patronymic: e.target.value }))
                        }
                      />
                    </label>
                    <label className="field">
                      Ник (необязательно)
                      <input
                        type="text"
                        value={editDraft.profile_nickname}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, profile_nickname: e.target.value }))
                        }
                      />
                    </label>
                    <label className="field">
                      Телефон (пусто — только если акция с телефоном не пройдена)
                      <input
                        type="tel"
                        value={editDraft.profile_phone}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, profile_phone: e.target.value }))
                        }
                      />
                    </label>
                    <label className="field">
                      Дата рождения
                      <input
                        type="date"
                        value={editDraft.profile_birth_date}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, profile_birth_date: e.target.value }))
                        }
                      />
                    </label>
                    <label className="field">
                      Баллы (бонусы)
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        value={editDraft.bonus_balance}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, bonus_balance: e.target.value }))
                        }
                      />
                    </label>
                    {editError ? <p className="errorMessage">{editError}</p> : null}
                    <div className="adminItemActions">
                      <Button type="button" variant="accent" disabled={saving} onClick={saveEdit}>
                        {saving ? "Сохранение…" : "Сохранить"}
                      </Button>
                      <Button type="button" disabled={saving} onClick={cancelEdit}>
                        Отмена
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <h4>
                        {[row.profile_last_name, row.profile_first_name, row.profile_patronymic]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </h4>
                      <p className="muted">
                        Telegram id: {row.telegram_id}
                        {row.username ? ` · @${row.username}` : ""}
                        {row.first_name || row.last_name
                          ? ` · в TG: ${[row.first_name, row.last_name].filter(Boolean).join(" ")}`
                          : ""}
                        {row.profile_nickname ? ` · ник: ${row.profile_nickname}` : ""}
                      </p>
                      <p className="muted">
                        <strong>Роль:</strong>{" "}
                        <span className={row.is_hostess ? "adminGuestBase__hostessLabel" : ""}>
                          {row.is_hostess ? "хостес" : "гость"}
                        </span>
                      </p>
                      {!hostessMode ? (
                        <p>
                          <strong>Бонусы:</strong>{" "}
                          <button
                            type="button"
                            className="adminGuestBase__bonusValue adminGuestBase__bonusValueBtn"
                            onClick={() => onToggleBonusHistory(row)}
                          >
                            {row.bonus_balance}
                          </button>
                        </p>
                      ) : null}
                      {!hostessMode && openedHistoryUserId === row.id ? (
                        <div className="adminGuestBase__history">
                          <p className="muted">История бонусов</p>
                          {loadingHistoryUserId === row.id ? (
                            <p className="muted">Загрузка...</p>
                          ) : null}
                          {loadingHistoryUserId !== row.id &&
                          (historyByUserId[row.id] || []).length === 0 ? (
                            <p className="muted">Операций пока нет.</p>
                          ) : null}
                          {loadingHistoryUserId !== row.id &&
                          (historyByUserId[row.id] || []).length > 0 ? (
                            <ul className="adminGuestBase__historyList">
                              {(historyByUserId[row.id] || []).map((tx) => {
                                const isDebit =
                                  tx.kind === "debit_redeem" ||
                                  tx.kind === "ch_forfeit" ||
                                  (tx.kind === "admin_adj" && tx.amount < 0);
                                return (
                                  <li key={tx.id}>
                                    <span
                                      className={
                                        isDebit
                                          ? "bonusHistory__amount bonusHistory__amount--debit"
                                          : "bonusHistory__amount"
                                      }
                                    >
                                      {isDebit ? "−" : "+"}
                                      {Math.abs(tx.amount)}
                                    </span>
                                    <span className="bonusHistory__meta">
                                      {getBonusKindLabel(tx.kind)}
                                      {tx.balance_after != null ? ` · баланс ${tx.balance_after}` : ""}
                                      {tx.created_at
                                        ? ` · ${formatIrkutskDateTime(tx.created_at)}`
                                        : ""}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}
                      {!hostessMode && row.profile_phone ? (
                        <p>
                          <strong>Телефон:</strong> {row.profile_phone}
                        </p>
                      ) : null}
                      {!hostessMode && row.profile_birth_date ? (
                        <p>
                          <strong>Дата рождения:</strong>{" "}
                          {String(row.profile_birth_date).slice(0, 10)}
                        </p>
                      ) : null}
                      {!hostessMode ? (
                        <p className="muted">
                          <strong>Канал (акция):</strong>{" "}
                          {row.promo_channel_completed_at ? "подписка подтверждена" : "—"}
                        </p>
                      ) : null}
                      {!hostessMode ? (
                        <p className="muted">
                          <strong>Отзывы:</strong> Яндекс{" "}
                          {row.promo_yandex_review_completed_at ? "да" : "—"} · 2ГИС{" "}
                          {row.promo_2gis_review_completed_at ? "да" : "—"}
                        </p>
                      ) : null}
                      {!hostessMode && row.bonus_sequestered_for_channel != null ? (
                        <div className="adminGuestBase__channelPenalty">
                          <p className="errorMessage">
                            Отписан от канала (ежемесячная проверка). Удержано бонусов:{" "}
                            <strong>{row.bonus_sequestered_for_channel}</strong>
                            {row.channel_unsubscribed_penalty_at
                              ? ` · с ${String(row.channel_unsubscribed_penalty_at).slice(0, 10)}`
                              : ""}
                          </p>
                          <Button
                            type="button"
                            variant="accent"
                            disabled={restoringChannelId === row.id}
                            onClick={() => onRestoreChannelBalance(row)}
                          >
                            {restoringChannelId === row.id
                              ? "Восстановление…"
                              : "Восстановить баланс"}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                    <div className="adminItemActions">
                      {canManageHostess ? (
                        <Button
                          type="button"
                          className={row.is_hostess ? "adminGuestBase__hostessRemoveBtn" : ""}
                          disabled={togglingHostessId === row.id}
                          onClick={() => onToggleHostess(row)}
                        >
                          {togglingHostessId === row.id
                            ? "Сохранение…"
                            : row.is_hostess
                              ? "Снять хостес"
                              : "Сделать хостес"}
                        </Button>
                      ) : null}
                      {!hostessMode ? (
                        <Button type="button" onClick={() => startEdit(row)}>
                          Изменить данные
                        </Button>
                      ) : null}
                      {!hostessMode && canManageHostess ? (
                        <Button
                          type="button"
                          className="adminGuestBase__deleteBtn"
                          disabled={deletingId === row.id}
                          onClick={() => onDeleteGuest(row)}
                        >
                          {deletingId === row.id ? "Удаление…" : "Удалить из базы"}
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
              </article>
            ))
          : null}
      </section>
    </>
  );
}
